/**
 * Chat Sidebar Component
 * 
 * Displays list of chat sessions for the Executive Co-Pilot.
 * Supports creating new chats, renaming, and deleting sessions.
 */
import React, { useState } from 'react';
import { 
  MessageSquare, Plus, MoreHorizontal, Pencil, Trash2, 
  ChevronLeft, ChevronRight, Loader2, Globe, Package
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { formatDistanceToNow } from 'date-fns';

// Format time with protection against future dates
const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  
  // If date is in the future (due to timezone issues), just show "just now"
  if (date > now) {
    return 'just now';
  }
  
  return formatDistanceToNow(date, { addSuffix: true });
};

// Session Item Component
const SessionItem = ({ 
  session, 
  isActive, 
  onClick, 
  onRename, 
  onDelete 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [showMenu, setShowMenu] = useState(false);

  const timeAgo = formatTimeAgo(session.updated_at);

  const getScopeIcon = () => {
    if (session.scope === 'all') {
      return <Globe className="w-3 h-3" />;
    }
    return <Package className="w-3 h-3" />;
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-1 px-3 py-2.5 rounded-lg cursor-pointer transition-colors",
        isActive 
          ? "bg-indigo-100 dark:bg-indigo-900/40" 
          : "hover:bg-gray-100 dark:hover:bg-gray-800"
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MessageSquare className={cn(
            "w-4 h-4 flex-shrink-0",
            isActive ? "text-indigo-600 dark:text-indigo-400" : colors.textMuted
          )} />
          <span className={cn(
            "text-sm font-medium truncate",
            isActive ? "text-indigo-700 dark:text-indigo-300" : colors.textPrimary
          )}>
            {session.title || 'New Chat'}
          </span>
        </div>
        
        <DropdownMenu open={showMenu} onOpenChange={setShowMenu}>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button 
              className={cn(
                "p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                "hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
            >
              <MoreHorizontal className={cn("w-4 h-4", colors.textMuted)} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onRename(session); }}>
              <Pencil className="w-4 h-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onDelete(session); }}
              className="text-red-600 dark:text-red-400"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="flex items-center gap-2 ml-6">
        <span className={cn(
          "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded",
          session.scope === 'all' 
            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
        )}>
          {getScopeIcon()}
          <span className="truncate max-w-[100px]">
            {session.scope_label || (session.scope === 'all' ? 'All Products' : 'Product')}
          </span>
        </span>
        <span className={cn("text-xs", colors.textMuted)}>{timeAgo}</span>
      </div>
    </div>
  );
};

// Main Sidebar Component
const ChatSidebar = ({
  sessions: sessionsProp,
  activeSessionId,
  isLoading,
  isCollapsed,
  onToggleCollapse,
  onSelectSession,
  onNewChat,
  onRenameSession,
  onDeleteSession,
  className
}) => {
  // Ensure sessions is always an array
  const sessions = Array.isArray(sessionsProp) ? sessionsProp : [];
  
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [renameDialog, setRenameDialog] = useState({ open: false, session: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, session: null });
  const [newTitle, setNewTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  const handleRename = (session) => {
    setNewTitle(session.title || '');
    setRenameDialog({ open: true, session });
  };

  const handleConfirmRename = async () => {
    if (!newTitle.trim() || !renameDialog.session) return;
    setIsRenaming(true);
    try {
      await onRenameSession(renameDialog.session.session_id, newTitle.trim());
      setRenameDialog({ open: false, session: null });
      setNewTitle('');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = (session) => {
    setDeleteDialog({ open: true, session });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.session) return;
    setIsDeleting(true);
    try {
      await onDeleteSession(deleteDialog.session.session_id);
      setDeleteDialog({ open: false, session: null });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isCollapsed) {
    return (
      <div className={cn(
        "flex flex-col items-center py-4 border-r",
        colors.bgCard, colors.borderPrimary,
        className
      )}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="mb-4"
        >
          <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onNewChat}
          className="mb-2"
        >
          <Plus className={cn("w-5 h-5", colors.textAccent)} />
        </Button>
        <div className="flex-1 flex flex-col items-center gap-1 overflow-y-auto w-full px-2">
          {sessions.slice(0, 10).map((session) => (
            <button
              key={session.session_id}
              onClick={() => onSelectSession(session)}
              className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                activeSessionId === session.session_id
                  ? "bg-indigo-100 dark:bg-indigo-900/40"
                  : "hover:bg-gray-100 dark:hover:bg-gray-800"
              )}
              title={session.title}
            >
              <MessageSquare className={cn(
                "w-4 h-4",
                activeSessionId === session.session_id 
                  ? "text-indigo-600 dark:text-indigo-400" 
                  : colors.textMuted
              )} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cn(
        "flex flex-col h-full border-r",
        colors.bgCard, colors.borderPrimary,
        className
      )}>
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between px-3 py-3 border-b",
          colors.borderPrimary
        )}>
          <span className={cn("text-sm font-semibold", colors.textPrimary)}>Threads</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onNewChat}
              className="h-8 w-8"
              title="New Chat"
            >
              <Plus className={cn("w-4 h-4", colors.textAccent)} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-8 w-8"
            >
              <ChevronLeft className={cn("w-4 h-4", colors.textMuted)} />
            </Button>
          </div>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className={cn("w-5 h-5 animate-spin", colors.textMuted)} />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 px-3">
              <MessageSquare className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
              <p className={cn("text-sm", colors.textSecondary)}>No conversations yet</p>
              <p className={cn("text-xs mt-1", colors.textMuted)}>
                Start a new chat to get insights
              </p>
            </div>
          ) : (
            sessions.map((session) => (
              <SessionItem
                key={session.session_id}
                session={session}
                isActive={activeSessionId === session.session_id}
                onClick={() => onSelectSession(session)}
                onRename={handleRename}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </div>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={(open) => !open && setRenameDialog({ open: false, session: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Conversation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter new title"
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmRename()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog({ open: false, session: null })}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRename} disabled={!newTitle.trim() || isRenaming}>
              {isRenaming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, session: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Conversation</DialogTitle>
          </DialogHeader>
          <p className={cn("py-4 text-sm", colors.textSecondary)}>
            Are you sure you want to delete "{deleteDialog.session?.title}"? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, session: null })}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatSidebar;
